import * as React from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import {
  usePreviewSoapMutation,
  type SoapContractPreview as SoapContractPreviewData
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { soapAuthFields, soapAuthUsesPassword } from './soap-auth'
import type { PrepValues } from './types'

export function soapPreviewAlert(error: unknown): { title: string; detail: string } {
  const data =
    error && typeof error === 'object' && 'data' in error
      ? (error as { data?: unknown }).data
      : undefined
  const record =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const reason = typeof record.reason === 'string' ? record.reason : ''
  const message = typeof record.error === 'string' ? record.error : ''
  const raw = `${reason} ${message}`

  if (reason === 'SOAP_AUTH_FAILED' || /rejected these credentials/i.test(raw)) {
    return {
      title: 'Authentication failed',
      detail: message || 'The SOAP service rejected these credentials.'
    }
  }
  if (reason === 'SOAP_CONTRACT_UNSUPPORTED') {
    return {
      title: 'Unsupported WSDL',
      detail: message || 'This WSDL is not a supported SOAP contract.'
    }
  }
  if (reason === 'SOAP_PROBE_FAILED') {
    return {
      title: 'Could not preview this WSDL',
      detail: message || 'Could not probe this WSDL.'
    }
  }
  if (/Proxy POST|Unable to probe SOAP/i.test(raw)) {
    return {
      title: 'Could not preview this WSDL',
      detail: 'Check the URL, credentials, and that the SOAP service is running.'
    }
  }
  return {
    title: 'Could not preview this WSDL',
    detail: message || 'Could not probe this WSDL.'
  }
}

function operationKey(operation: SoapContractPreviewData['operations'][number]): string {
  return (
    operation.operationId ??
    [operation.binding, operation.port, operation.name].filter(Boolean).join(':')
  )
}

function selectedOperations(prep: PrepValues): Set<string> {
  try {
    const value = JSON.parse(prep.fields.enabledOperations ?? '[]')
    return new Set(
      Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
    )
  } catch {
    return new Set()
  }
}

export function SoapContractPreview({
  prep,
  onSelectionChange
}: {
  prep: PrepValues
  onSelectionChange: (operations: string[], contractHash?: string) => void
}) {
  const [previewSoap, previewState] = usePreviewSoapMutation()
  const [preview, setPreview] = React.useState<SoapContractPreviewData | null>(null)
  const { authKind, username, password } = soapAuthFields(prep)
  const endpointName = prep.fields.endpointName?.trim() ?? ''
  const wsdlUrl = prep.fields.wsdlUrl?.trim() ?? ''
  const selected = selectedOperations(prep)
  const alert = previewState.isError ? soapPreviewAlert(previewState.error) : null
  const ready =
    endpointName.length > 0 &&
    wsdlUrl.length > 0 &&
    (!soapAuthUsesPassword(authKind) || (!!username && !!password))

  const runPreview = () => {
    setPreview(null)
    onSelectionChange([])
    void previewSoap({
      endpointName,
      wsdlUrl,
      authKind,
      ...(soapAuthUsesPassword(authKind) ? { username, password } : {})
    })
      .unwrap()
      .then(setPreview)
  }

  return (
    <div className="rounded-md border border-line-2 bg-bg-sunk px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-ink">Review WSDL contract</div>
          <p className="mt-0.5 text-10 leading-snug text-ink-3">
            Probe without creating an ingest job. Start ingest performs registration.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!ready || previewState.isLoading}
          onClick={runPreview}
        >
          {previewState.isLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
          Preview operations
        </Button>
      </div>

      {alert ? (
        <div
          role="alert"
          className="mt-2 rounded-md border border-st-down/30 bg-st-down-bg px-3 py-2 text-st-down"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <div>
              <div className="text-xs font-medium">{alert.title}</div>
              <p className="mt-0.5 text-11 leading-snug text-st-down/90">{alert.detail}</p>
            </div>
          </div>
        </div>
      ) : null}

      {preview ? (
        <div className="mt-2.5 border-t border-line pt-2.5">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-10 text-ink-3">
            <span>
              Service:{' '}
              <strong className="font-medium text-ink-2">
                {preview.endpoint.serviceName ?? endpointName}
              </strong>
            </span>
            <span>{preview.operations.length} operation(s)</span>
            <span className="font-mono">Contract {preview.contractHash.slice(0, 12)}</span>
          </div>
          {preview.operations.length > 0 ? (
            <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-11 font-medium text-ink-2">
              <Checkbox
                checked={
                  preview.operations.every((operation) => selected.has(operationKey(operation)))
                    ? true
                    : preview.operations.some((operation) => selected.has(operationKey(operation)))
                      ? 'indeterminate'
                      : false
                }
                aria-label="Select all SOAP operations"
                onCheckedChange={(checked) => {
                  onSelectionChange(
                    checked === true ? preview.operations.map(operationKey) : [],
                    preview.contractHash
                  )
                }}
              />
              Select all operations
            </label>
          ) : null}
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {preview.operations.map((operation) => (
              <li
                key={operation.operationId ?? `${operation.binding ?? ''}:${operation.name}`}
                className="flex items-start gap-2 rounded border border-line bg-bg-elev px-2 py-1.5"
              >
                <Checkbox
                  checked={selected.has(operationKey(operation))}
                  aria-label={`Enable ${operation.name} for agent calls`}
                  onCheckedChange={(checked) => {
                    const next = new Set(selected)
                    if (checked === true) next.add(operationKey(operation))
                    else next.delete(operationKey(operation))
                    onSelectionChange([...next], preview.contractHash)
                  }}
                />
                <div className="min-w-0">
                  <div className="truncate text-11 font-medium text-ink">{operation.name}</div>
                  {operation.soapAction ? (
                    <div className="truncate font-mono text-9 text-ink-3">
                      {operation.soapAction}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-10 leading-snug text-ink-3">
            Select only operations you have confirmed are read-only. Selected operations become
            available to agents.
          </p>
        </div>
      ) : null}
    </div>
  )
}
